import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type AvatarStatus = 'online' | 'offline' | 'away' | 'busy' | '';

/**
 * Displays a user's identity using a circular media frame (image or SVG), with
 * automatic fallback to text initials when no media is slotted. Optionally shows
 * a presence status indicator dot at the bottom-right of the frame, and supports
 * inline name and sub-label metadata rendered to the right of the circle.
 *
 * Use in profile cards, comment threads, chat interfaces, and data tables.
 *
 * @element asc-avatar
 *
 * @slot - Primary media — accepts an `<img>` or an `<svg>`. When neither is present,
 *   the `initials` property value is displayed instead.
 * @slot name - User's full name, displayed to the right of the avatar circle.
 * @slot sublabel - Sub-label text (e.g. email address or publication date), displayed below the name slot.
 *
 * @csspart base - The outer flex container wrapping the frame and info block.
 * @csspart frame - The circular element that backgrounds/clips the media or initials.
 * @csspart initials - The `<span>` rendering the fallback initials text.
 * @csspart status - The presence indicator dot at the bottom-right of the frame.
 * @csspart info - The flex column containing the name and sublabel slots.
 *
 * @cssprop --asc-avatar-bg - Frame background when showing initials. Default: `var(--asc-bg-tertiary)`
 * @cssprop --asc-avatar-fg - Initials text color. Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-avatar-border-radius - Frame border radius. Default: `var(--asc-radius-full)`
 * @cssprop --asc-avatar-border-color - Frame border color. Default: `var(--asc-border-secondary)` when showing initials; `var(--asc-border-tertiary)` when media is slotted.
 * @cssprop --asc-avatar-border-width - Frame border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-avatar-size-sm - Override the frame size at the `sm` step. Default: `32px`
 * @cssprop --asc-avatar-size-md - Override the frame size at the `md` step. Default: `40px`
 * @cssprop --asc-avatar-size-lg - Override the frame size at the `lg` step. Default: `48px`
 * @cssprop --asc-avatar-size-xl - Override the frame size at the `xl` step. Default: `56px`
 * @cssprop --asc-avatar-size-2xl - Override the frame size at the `2xl` step. Default: `64px`
 * @cssprop --asc-avatar-status-online-color - Status dot color for online. Default: `var(--asc-fg-success-secondary)`
 * @cssprop --asc-avatar-status-offline-color - Status dot color for offline. Default: `var(--asc-fg-disabled_subtle)`
 * @cssprop --asc-avatar-status-away-color - Status dot color for away. Default: `var(--asc-bg-warning-solid)`
 * @cssprop --asc-avatar-status-busy-color - Status dot color for busy. Default: `var(--asc-bg-error-solid)`
 * @cssprop --asc-avatar-name-color - Name text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-avatar-name-font-size - Name font size override. Defaults to a size-driven scale: `text-xs` at xs/sm fallback, `text-sm` at sm/md, `text-md` at lg, `text-lg` at xl, `text-xl` at 2xl.
 * @cssprop --asc-avatar-name-font-weight - Name font weight. Default: `var(--asc-font-weight-semibold)`
 * @cssprop --asc-avatar-sublabel-color - Sub-label text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-avatar-sublabel-font-size - Sub-label font size override. Defaults to a size-driven scale: `text-xs` at xs/sm, `text-sm` at md, `text-md` at lg/xl, `text-lg` at 2xl.
 * @cssprop --asc-avatar-gap - Gap between the frame and the info block. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-avatar-ring-shadow - Box shadow applied to the frame. Used by `asc-avatar-group` to render a separator ring around each stacked avatar. Default: `none`
 * @cssprop --asc-avatar-info-display - Display value for the info block (name + email). Set to `none` by `asc-avatar-group` to suppress labels inside the stack. Default: `flex`
 */
@customElement('asc-avatar')
export class AscAvatar extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;
      font-family: var(--asc-font-family-body);

      /* Internal label typography variables — driven by size, overridable via
       * --asc-avatar-name-font-size / --asc-avatar-sublabel-font-size.
       * Default values match the sm size (the component default). */
      --_name-size: var(--asc-font-size-text-sm);
      --_name-lh:   var(--asc-line-height-text-sm);
      --_sublabel-size: var(--asc-font-size-text-xs);
      --_sublabel-lh:   var(--asc-line-height-text-xs);
    }

    /* ── Base layout ──────────────────────────────────────────── */
    .base {
      display: inline-flex;
      align-items: center;
      gap: var(--asc-avatar-gap, var(--asc-spacing-md));
    }

    /* Gap scales with avatar size (per Figma) */
    :host([size='lg'])  .base { gap: var(--asc-spacing-lg); }
    :host([size='xl'])  .base { gap: var(--asc-spacing-xl); }
    :host([size='2xl']) .base { gap: var(--asc-spacing-xl); }

    /* Label typography — per-size overrides (per Figma) */
    :host([size='xs']) {
      --_name-size:     var(--asc-font-size-text-xs);
      --_name-lh:       var(--asc-line-height-text-xs);
      --_sublabel-size: var(--asc-font-size-text-xs);
      --_sublabel-lh:   var(--asc-line-height-text-xs);
    }
    /* size='sm' matches the :host defaults above */
    :host([size='md']) {
      --_name-size:     var(--asc-font-size-text-sm);
      --_name-lh:       var(--asc-line-height-text-sm);
      --_sublabel-size: var(--asc-font-size-text-sm);
      --_sublabel-lh:   var(--asc-line-height-text-sm);
    }
    :host([size='lg']) {
      --_name-size:     var(--asc-font-size-text-md);
      --_name-lh:       var(--asc-line-height-text-md);
      --_sublabel-size: var(--asc-font-size-text-md);
      --_sublabel-lh:   var(--asc-line-height-text-md);
    }
    :host([size='xl']) {
      --_name-size:     var(--asc-font-size-text-lg);
      --_name-lh:       var(--asc-line-height-text-lg);
      --_sublabel-size: var(--asc-font-size-text-md);
      --_sublabel-lh:   var(--asc-line-height-text-md);
    }
    :host([size='2xl']) {
      --_name-size:     var(--asc-font-size-text-xl);
      --_name-lh:       var(--asc-line-height-text-xl);
      --_sublabel-size: var(--asc-font-size-text-lg);
      --_sublabel-lh:   var(--asc-line-height-text-lg);
    }

    /* ── Frame ────────────────────────────────────────────────── */

    /*
     * NOTE: overflow is intentionally NOT set to hidden on the frame.
     * The status badge is absolutely positioned at −1 px offsets, so it must
     * overflow the frame boundary. Slotted <img> elements achieve the circular
     * crop via their own border-radius (see ::slotted(img) below).
     */
    .frame {
      position: relative;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      /* Default frame size (sm = 32 px) — overridden per-size below */
      width: var(--asc-avatar-size-sm, 32px);
      height: var(--asc-avatar-size-sm, 32px);

      border-style: solid;
      border-width: var(--asc-avatar-border-width, var(--asc-border-width));
      border-radius: var(--asc-avatar-border-radius, var(--asc-radius-full));

      /*
       * --asc-avatar-ring-shadow is set by a parent component (e.g. asc-avatar-group)
       * via ::slotted(asc-avatar) { --asc-avatar-ring-shadow: ... } so the ring
       * follows this element's border-radius rather than appearing on the host box.
       */
      box-shadow: var(--asc-avatar-ring-shadow, none);
    }

    /* Frame colours — differ based on whether media or initials are shown */
    .frame--initials {
      background-color: var(--asc-avatar-bg, var(--asc-bg-tertiary));
      border-color: var(--asc-avatar-border-color, var(--asc-border-secondary));
    }

    .frame--media {
      background-color: transparent;
      border-color: var(--asc-avatar-border-color, var(--asc-border-tertiary));
    }

    /* ── Frame sizes ──────────────────────────────────────────── */
    :host([size='xs'])  .frame { width: 24px;                               height: 24px; }
    :host([size='sm'])  .frame { width: var(--asc-avatar-size-sm, 32px);   height: var(--asc-avatar-size-sm,  32px); }
    :host([size='md'])  .frame { width: var(--asc-avatar-size-md, 40px);   height: var(--asc-avatar-size-md,  40px); }
    :host([size='lg'])  .frame { width: var(--asc-avatar-size-lg, 48px);   height: var(--asc-avatar-size-lg,  48px); }
    :host([size='xl'])  .frame { width: var(--asc-avatar-size-xl, 56px);   height: var(--asc-avatar-size-xl,  56px); }
    :host([size='2xl']) .frame { width: var(--asc-avatar-size-2xl, 64px);  height: var(--asc-avatar-size-2xl, 64px); }

    /* ── Slotted media ────────────────────────────────────────── */
    ::slotted(img) {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      max-width: none;
      object-fit: cover;
      pointer-events: none;
      display: block;
      /* Clips the image to the circle without needing overflow:hidden on the frame */
      border-radius: var(--asc-radius-full);
    }

    /*
     * SVGs slot in as centered icons (Placeholder / Logo pattern from Figma).
     * They are NOT stretched to fill the frame — the frame's flexbox centers them.
     * Sizing at ~60 % matches the Placeholder icon proportions across all sizes.
     * The SVG inherits the initials foreground token so icon color stays consistent.
     * Consumers can override width/height directly on the slotted <svg> element.
     */
    ::slotted(svg) {
      display: block;
      width: 60%;
      height: 60%;
      flex-shrink: 0;
      pointer-events: none;
      color: var(--asc-avatar-fg, var(--asc-text-quaternary));
    }

    /* ── Initials ─────────────────────────────────────────────── */
    .initials {
      color: var(--asc-avatar-fg, var(--asc-text-quaternary));
      font-family: var(--asc-font-family-body);
      font-weight: var(--asc-font-weight-semibold);
      text-align: center;
      user-select: none;
    }

    /* Initials font size grows with avatar size (matches Figma type scale) */
    :host([size='xs'])  .initials { font-size: var(--asc-font-size-text-xs);      line-height: var(--asc-line-height-text-xs); }
    :host([size='sm'])  .initials { font-size: var(--asc-font-size-text-sm);      line-height: var(--asc-line-height-text-sm); }
    :host([size='md'])  .initials { font-size: var(--asc-font-size-text-md);      line-height: var(--asc-line-height-text-md); }
    :host([size='lg'])  .initials { font-size: var(--asc-font-size-text-lg);      line-height: var(--asc-line-height-text-lg); }
    :host([size='xl'])  .initials { font-size: var(--asc-font-size-text-xl);      line-height: var(--asc-line-height-text-xl); }
    :host([size='2xl']) .initials {
      font-family: var(--asc-font-family-display);
      font-size: var(--asc-font-size-display-xs);
      line-height: var(--asc-line-height-display-xs);
    }

    /* ── Status badge ─────────────────────────────────────────── */
    .status {
      position: absolute;
      border-radius: var(--asc-radius-full);
      /* White ring separates the dot from the frame border (1.5 px, per Figma) */
      border: 1.5px solid var(--asc-bg-primary, #ffffff);
      /* Default sizing for sm (overridden per size below) */
      width: 8px;
      height: 8px;
      bottom: -0.75px;
      right: -0.75px;
    }

    /* Status badge size and offset scale with the avatar frame */
    :host([size='xs'])  .status { width: 6px;  height: 6px;  bottom: -0.5px; right: -0.5px; }
    :host([size='sm'])  .status { width: 8px;  height: 8px;  bottom: -0.75px; right: -0.75px; }
    :host([size='md'])  .status { width: 10px; height: 10px; bottom: -1px;   right: -1px; }
    :host([size='lg'])  .status { width: 12px; height: 12px; bottom: -1px;   right: -1px; }
    :host([size='xl'])  .status { width: 14px; height: 14px; bottom: -1px;   right: -1px; }
    :host([size='2xl']) .status { width: 16px; height: 16px; bottom: -1px;   right: -1px; }

    /* Status indicator colours (applied via host attribute selectors) */
    :host([status='online'])  .status { background-color: var(--asc-avatar-status-online-color,  var(--asc-fg-success-secondary)); }
    :host([status='offline']) .status { background-color: var(--asc-avatar-status-offline-color, var(--asc-fg-disabled_subtle)); }
    :host([status='away'])    .status { background-color: var(--asc-avatar-status-away-color,    var(--asc-bg-warning-solid)); }
    :host([status='busy'])    .status { background-color: var(--asc-avatar-status-busy-color,    var(--asc-bg-error-solid)); }

    /* ── Info block (name + email) ────────────────────────────── */
    .info {
      /*
       * --asc-avatar-info-display allows a parent (e.g. asc-avatar-group) to
       * suppress the info block via ::slotted(asc-avatar) { --asc-avatar-info-display: none }
       * without reaching into this shadow root directly.
       */
      display: var(--asc-avatar-info-display, flex);
      flex-direction: column;
      align-items: flex-start;
    }

    .info--hidden {
      display: none;
    }

    /* Typography for slotted name content */
    slot[name='name']::slotted(*) {
      color: var(--asc-avatar-name-color, var(--asc-text-primary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-avatar-name-font-size, var(--_name-size));
      font-weight: var(--asc-avatar-name-font-weight, var(--asc-font-weight-semibold));
      line-height: var(--_name-lh);
      display: block;
    }

    /* Typography for slotted sublabel content */
    slot[name='sublabel']::slotted(*) {
      color: var(--asc-avatar-sublabel-color, var(--asc-text-tertiary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-avatar-sublabel-font-size, var(--_sublabel-size));
      font-weight: var(--asc-font-weight-regular);
      line-height: var(--_sublabel-lh);
      display: block;
    }
  `;

  /** The size of the avatar frame. Controls all dimensional tokens via `:host([size])` selectors. */
  @property({ type: String, reflect: true }) size: AvatarSize = 'sm';

  /** Fallback text (typically 1–2 characters) shown inside the frame when no `<img>` or `<svg>` is slotted. */
  @property({ type: String }) initials = '';

  /** Presence status. When set to a non-empty value, renders a colored indicator dot at the bottom-right of the frame. */
  @property({ type: String, reflect: true }) status: AvatarStatus = '';

  /**
   * `true` when the default slot contains an `<img>` or `<svg>` element.
   * Controls whether initials are suppressed.
   */
  @state() private _hasMedia = false;

  /**
   * `true` only when the default slot contains an `<img>` element.
   * Controls the `frame--media` class (transparent background + tertiary border)
   * vs `frame--initials` (grey bg + secondary border).
   * SVGs stay on the grey background so contained icons (Placeholder variant)
   * show the frame background behind them — matching the Figma spec.
   */
  @state() private _hasCoverImg = false;

  /** `true` when the `name` or `sublabel` slot has assigned nodes. Controls visibility of the info block. */
  @state() private _hasInfo = false;

  @query('slot[name="name"]') private _nameSlot!: HTMLSlotElement;
  @query('slot[name="sublabel"]') private _sublabelSlot!: HTMLSlotElement;

  private _handleMediaSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    const assigned = slot.assignedElements();
    this._hasMedia = assigned.some((el) => el.tagName === 'IMG' || el.tagName === 'SVG');
    this._hasCoverImg = assigned.some((el) => el.tagName === 'IMG');
  }

  private _handleInfoSlotChange(): void {
    const nameLen = this._nameSlot?.assignedNodes({ flatten: true }).length ?? 0;
    const sublabelLen = this._sublabelSlot?.assignedNodes({ flatten: true }).length ?? 0;
    this._hasInfo = nameLen > 0 || sublabelLen > 0;
  }

  override render() {
    const frameLabel = this._hasMedia ? '' : this.initials || 'Avatar';
    // frame--media (transparent bg) only for <img>; SVG slots keep the grey bg.
    const frameClass = `frame ${this._hasCoverImg ? 'frame--media' : 'frame--initials'}`;
    const infoClass = `info${this._hasInfo ? '' : ' info--hidden'}`;

    return html`
      <div part="base" class="base">
        <div
          part="frame"
          class=${frameClass}
          role=${this._hasMedia ? nothing : 'img'}
          aria-label=${this._hasMedia ? nothing : frameLabel}
        >
          <slot @slotchange=${this._handleMediaSlotChange}></slot>
          ${!this._hasMedia
            ? html`<span part="initials" class="initials" aria-hidden="true">${this.initials}</span>`
            : ''}
          ${this.status
            ? html`<span
                part="status"
                class="status"
                role="img"
                aria-label=${'Status: ' + this.status}
              ></span>`
            : ''}
        </div>
        <div part="info" class=${infoClass}>
          <slot name="name" @slotchange=${this._handleInfoSlotChange}></slot>
          <slot name="sublabel" @slotchange=${this._handleInfoSlotChange}></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-avatar': AscAvatar;
  }
}
